import { useCallback, useEffect } from "react";
import FormItem from "@components/FormItem/FormItem";
import CustomFormLabel from "@components/FormItem/FormItem.styles";
import FormSection from "@components/FormSection/FormSection";
import type { NormalGatepassFormData } from "@features/gatepass/schema/normalGatepassFormSchema";
import { useAuthStore } from "@persisted-state/auth/auth.store";
import { t } from "i18next";
import { FormProvider, type UseFormReturn } from "react-hook-form";
import { useOutletContext } from "react-router-dom";
import { useNormalGatepassFormStore } from "../../store/useNormalGatepassForm.store";

export interface FormContext {
  formData: UseFormReturn<NormalGatepassFormData>;
  updateFormData: (key: string, value: unknown) => void;
}
const TRANSLATION_KEY = "gatepass";
const INDIVIDUAL_ROLES = ["security-applicant", "uae-pass-user"];
const VIP_ALLOWED_ROLES = ["security-operator", "security-supervisor"];

const GatepassDetails = () => {
  const { formData, updateFormData } = useOutletContext<FormContext>();
  const { isFottDisabled } = useNormalGatepassFormStore();
  const userRoles = useAuthStore((state) => state.user?.roles ?? []);
  const isIndividualRole = INDIVIDUAL_ROLES.some((role) => userRoles.includes(role));
  const canRequestVip = VIP_ALLOWED_ROLES.some((role) => userRoles.includes(role));
  const selectedRequestFor = formData.watch("requestFor");
  const selectedGatepassDuration = formData.watch("gatepassDuration");
  const selectedEntryType = formData.watch("entryType");
  const selectedVip = formData.watch("vip");
  const selectedFottEntry = formData.watch("FOTTEntry");
  const isExitRequest = !isIndividualRole && selectedRequestFor === "exit";
  const isEntryTypeForcedMultiple =
    !isExitRequest &&
    (selectedGatepassDuration === "weekly" || selectedGatepassDuration === "permanent");
  const isVipForcedNo =
    !isIndividualRole && (isExitRequest || selectedGatepassDuration === "permanent");
  const isFottEntryDisabled = isFottDisabled && selectedFottEntry !== true;

  const gatepassDurationOptions = [
    { label: t("common.daily"), value: "daily" },
    { label: t("common.weekly"), value: "weekly" },
    ...(!isIndividualRole ? [{ label: t("common.permanent"), value: "permanent" }] : []),
  ];
  const permanentDurationOptions = [
    { label: t("common.1-month"), value: "1" },
    { label: t("common.3-months"), value: "3" },
    { label: t("common.6-months"), value: "6" },
    { label: t("common.12-months"), value: "12" },
  ];
  const gatepassTypeOptions = [
    { label: t("common.single"), value: "single" },
    { label: t("common.group"), value: "group" },
  ];
  const vipOptions = [
    { label: t("common.yes"), value: true },
    { label: t("common.no"), value: false },
  ];
  const requestForOptions = [
    { label: t("common.entry"), value: "entry" },
    { label: t("common.exit"), value: "exit" },
  ];
  const entryTypeOptions = [
    { label: t("common.single"), value: "single" },
    { label: t("common.multiple"), value: "multiple" },
  ];
  const FOTTEntryOptions = [
    { label: t("common.yes"), value: true },
    { label: t("common.no"), value: false },
  ];

  useEffect(() => {
    if (!isExitRequest) return;

    if (selectedGatepassDuration !== "daily") {
      formData.setValue("gatepassDuration", "daily", {
        shouldDirty: true,
        shouldValidate: true,
      });
      formData.clearErrors("gatepassDuration");
      updateFormData("gatepassDuration", "daily");
    }

    if (selectedEntryType !== "single") {
      formData.setValue("entryType", "single", {
        shouldDirty: true,
        shouldValidate: true,
      });
      formData.clearErrors("entryType");
      updateFormData("entryType", "single");
    }
  }, [formData, isExitRequest, selectedEntryType, selectedGatepassDuration, updateFormData]);

  useEffect(() => {
    if (!isEntryTypeForcedMultiple || selectedEntryType === "multiple") return;

    formData.setValue("entryType", "multiple", {
      shouldDirty: true,
      shouldValidate: true,
    });
    formData.clearErrors("entryType");
    updateFormData("entryType", "multiple");
  }, [formData, isEntryTypeForcedMultiple, selectedEntryType, updateFormData]);

  useEffect(() => {
    if (!isVipForcedNo || selectedVip === false) return;

    formData.setValue("vip", false, {
      shouldDirty: true,
      shouldValidate: true,
    });
    formData.clearErrors("vip");
    updateFormData("vip", false);
  }, [formData, isVipForcedNo, selectedVip, updateFormData]);

  const handleGatepassTypeChange = useCallback(
    (key: keyof NormalGatepassFormData, value: unknown) => {
      updateFormData(key, value);

      if (value === "single") {
        formData.setValue("groupMembers", [], {
          shouldDirty: true,
          shouldValidate: true,
        });
        formData.clearErrors("groupMembers");
        updateFormData("groupMembers", []);
      }
    },
    [formData, updateFormData],
  );

  return (
    <FormProvider {...formData}>
      <FormSection title={t(`${TRANSLATION_KEY}.gatepass-details`)}>
        {!isIndividualRole && (
          <>
            <div className="col-span-full flex">
              <FormItem<NormalGatepassFormData, "gatepassType">
                name="gatepassType"
                label={t(`${TRANSLATION_KEY}.type-of-gatepass`)}
                type="radio"
                options={gatepassTypeOptions}
                testId="radio-gatepassType"
                onFieldChange={handleGatepassTypeChange}
                horizontalRadioButtons
                inlineRadioButtons
                radioValidationBelowButtons
              />
            </div>
            <div className="col-span-full flex">
              <FormItem<NormalGatepassFormData, "requestFor">
                name="requestFor"
                label={t(`${TRANSLATION_KEY}.request-for`)}
                type="radio"
                options={requestForOptions}
                testId="radio-requestFor"
                onFieldChange={updateFormData}
                horizontalRadioButtons
                inlineRadioButtons
                radioValidationBelowButtons
              />
            </div>
          </>
        )}
        <div className="col-span-full flex">
          <FormItem<NormalGatepassFormData, "gatepassDuration">
            name="gatepassDuration"
            label={t(`${TRANSLATION_KEY}.gatepass-duration`)}
            type="radio"
            options={gatepassDurationOptions}
            testId="radio-gatepassDuration"
            onFieldChange={updateFormData}
            horizontalRadioButtons
            inlineRadioButtons
            radioValidationBelowButtons
            disabled={isExitRequest}
            {...(!isIndividualRole
              ? {
                  inlineRadioGroupBasis: "372px",
                  inlineRadioOptionBasis: "32%",
                }
              : {})}
          />
        </div>
        {!isIndividualRole && !isExitRequest && selectedGatepassDuration === "permanent" && (
          <div className="col-span-full flex items-center mb-7">
            <CustomFormLabel as="span" sx={{ flex: 1 }}>
              {t(`${TRANSLATION_KEY}.permanent-duration`)}
            </CustomFormLabel>
            <div className="basis-[372px]">
              <FormItem<NormalGatepassFormData, "permanentDuration">
                required
                name="permanentDuration"
                label=""
                type="select"
                options={permanentDurationOptions}
                testId="select-permanentDuration"
                onFieldChange={updateFormData}
                showErrorMsg={false}
              />
            </div>
          </div>
        )}
        <div className="col-span-full flex">
          <FormItem<NormalGatepassFormData, "entryType">
            name="entryType"
            label={t(`${TRANSLATION_KEY}.entry-type`)}
            type="radio"
            options={entryTypeOptions}
            testId="radio-entryType"
            onFieldChange={updateFormData}
            horizontalRadioButtons
            inlineRadioButtons
            radioValidationBelowButtons
            disabled={isExitRequest || isEntryTypeForcedMultiple}
          />
        </div>
        {canRequestVip && (
          <div className="col-span-full flex">
            <FormItem<NormalGatepassFormData, "vip">
              name="vip"
              label={t(`${TRANSLATION_KEY}.vip`)}
              type="radio"
              options={vipOptions}
              testId="radio-vip"
              onFieldChange={updateFormData}
              horizontalRadioButtons
              inlineRadioButtons
              radioValidationBelowButtons
              disabled={isVipForcedNo}
            />
          </div>
        )}
        <div className="col-span-full flex">
          <FormItem<NormalGatepassFormData, "FOTTEntry">
            name="FOTTEntry"
            label={t(`${TRANSLATION_KEY}.fott-entry`)}
            type="radio"
            options={FOTTEntryOptions}
            testId="radio-FOTTEntry"
            onFieldChange={updateFormData}
            horizontalRadioButtons
            inlineRadioButtons
            radioValidationBelowButtons
            disabled={isFottEntryDisabled}
          />
        </div>
      </FormSection>
    </FormProvider>
  );
};

export default GatepassDetails;
